import { message, Form } from "antd";
import React, { useEffect, useState } from "react";
import { Card, Table, Button, Space, Popconfirm, Tag, Row, Col } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import routes from "../../config/routes/routes";
import axios from "axios";
import AntdModal from "../../components/common/AntdModal";
import AppButton from "../../components/common/AppButton";
import AppInput from "../../components/common/AppInput";

const LeaveTypes = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [showOtherLeaveType, setShowOtherLeaveType] = useState(false);
  const [form] = Form.useForm();
  
  const selectedLeaveType = Form.useWatch('name', form);

  useEffect(() => {
    document.title = routes.settings.leaveTypes.title;
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    if (selectedLeaveType === 'OTHER') {
      setShowOtherLeaveType(true);
    } else {
      setShowOtherLeaveType(false);
      form.setFieldValue('other_leave_type', undefined);
    }
  }, [selectedLeaveType, form]);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/settings/leave-types");
      let leaveTypesData = [];
      if (Array.isArray(res)) {
        leaveTypesData = res;
      } else if (Array.isArray(res?.data)) {
        leaveTypesData = res.data;
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        leaveTypesData = res.data.data;
      } else if (res?.data && typeof res.data === 'object') {
        leaveTypesData = res.data.data || [];
      }
      setLeaveTypes(leaveTypesData);
    } catch (error) {
      message.error("Failed to fetch leave types");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const leaveTypeName = values.name === 'OTHER' ? values.other_leave_type : values.name;
      const payload = {
        name: leaveTypeName,
        max_days: values.max_days ? parseInt(values.max_days) : null,
        is_paid: values.is_paid || false,
        description: values.description || "",
      };

      if (editingLeaveType) {
        await axios.put(`/settings/leave-types/${editingLeaveType.id}`, payload);
        message.success("Leave type updated successfully");
      } else {
        await axios.post("/settings/leave-types", payload);
        message.success("Leave type created successfully");
      }

      setShowModal(false);
      setEditingLeaveType(null);
      form.resetFields();
      fetchLeaveTypes();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to save leave type";
      message.error(errorMsg);
    }
  };

  const handleEdit = (record) => {
    setEditingLeaveType(record);
    const existingInList = leaveTypes.some(lt => lt.name === record.name);
    
    form.setFieldsValue({
      name: existingInList ? record.name : 'OTHER',
      other_leave_type: existingInList ? undefined : record.name,
      max_days: record.max_days,
      is_paid: record.is_paid === true || record.is_paid === "true" ? true : false,
      description: record.description || "",
    });
    setShowOtherLeaveType(!existingInList);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/settings/leave-types/${id}`);
      message.success("Leave type deleted successfully");
      fetchLeaveTypes();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete leave type";
      message.error(errorMsg);
    }
  };

  const openAddModal = () => {
    setEditingLeaveType(null);
    setShowOtherLeaveType(false);
    form.resetFields();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLeaveType(null);
    setShowOtherLeaveType(false);
    form.resetFields();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Max Days",
      dataIndex: "max_days",
      key: "max_days",
      width: 100,
      render: (days) => days ? `${days} days` : "Unlimited",
    },
    {
      title: "Paid",
      dataIndex: "is_paid",
      key: "is_paid",
      width: 100,
      render: (isPaid) => (
        <Tag color={isPaid ? "green" : "default"}>
          {isPaid ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this leave type?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid p-2">
      <Card
        title={<h5 className="fw-bold text-uppercase mb-0">Leave Types</h5>}
        extra={
          <AppButton
            text="Add Leave Type"
            iconBefore={<PlusOutlined />}
            onClick={openAddModal}
          />
        }
      >
        <Table
          columns={columns}
          dataSource={leaveTypes}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <AntdModal
        title={editingLeaveType ? "Edit Leave Type" : "Add Leave Type"}
        open={showModal}
        closeModal={closeModal}
        footer={[
          <AppButton
            key="cancel"
            text="Cancel"
            onClick={closeModal}
            danger
          />,
          <AppButton
            key="submit"
            text={editingLeaveType ? "Update" : "Create"}
            onClick={() => form.submit()}
            type="primary"
          />,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <AppInput
                type="select"
                label="Leave Type"
                name="name"
                rules={[{ required: true, message: "Leave type is required" }]}
                options={[
                  { label: "Annual Leave", value: "Annual Leave" },
                  { label: "Maternity Leave", value: "Maternity Leave" },
                  { label: "Sick Leave", value: "Sick Leave" },
                  { label: "Study Leave", value: "Study Leave" },
                  { label: "Compassionate Leave", value: "Compassionate Leave" },
                  ...leaveTypes
                    .filter(lt => {
                      const commonTypes = ["Annual Leave", "Maternity Leave", "Sick Leave", "Study Leave", "Compassionate Leave"];
                      return !commonTypes.includes(lt.name);
                    })
                    .map(lt => ({
                      label: lt.name,
                      value: lt.name,
                    })),
                  { label: "Other (Add Custom Leave Type)", value: "OTHER" },
                ]}
                inputAttributes={{
                  placeholder: "Select Leave Type",
                  showSearch: true,
                }}
              />
            </Col>

            <Col xs={24} md={12}>
              {showOtherLeaveType && (
                <AppInput
                  name="other_leave_type"
                  label="Custom Leave Type Name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the custom leave type name",
                    },
                  ]}
                  inputAttributes={{
                    placeholder: "Enter custom leave type name",
                  }}
                />
              )}
            </Col>
          </Row>

          <AppInput
            label="Maximum Days"
            name="max_days"
            type="number"
            inputAttributes={{
              placeholder: "Enter maximum days (leave empty for unlimited)",
              min: 0,
            }}
          />

          <AppInput
            type="select"
            label="Is Paid Leave"
            name="is_paid"
            rules={[{ required: true, message: "Please select if this is paid leave" }]}
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            inputAttributes={{
              placeholder: "Select Yes or No",
            }}
          />

          <AppInput
            label="Description"
            name="description"
            type="textarea"
            inputAttributes={{
              placeholder: "Enter description (optional)",
              rows: 3,
            }}
          />
        </Form>
      </AntdModal>
    </div>
  );
};

export default LeaveTypes;
